import { createContext, useCallback, useContext, useRef, useState, type ReactNode } from 'react';
import { useIsMobile } from '../lib/hooks';

interface ToastItem {
  id: number;
  message: ReactNode;
  undo?: () => void;
  action?: { label: string; run: () => void };
}
interface Ctx {
  toast: (
    message: ReactNode,
    opts?: { undo?: () => void; action?: { label: string; run: () => void }; ms?: number },
  ) => void;
  dismiss: () => void;
}

const ToastContext = createContext<Ctx>({ toast: () => {}, dismiss: () => {} });
export const useToast = () => useContext(ToastContext);

/** Ink toast with optional Undo (design-system §6). One at a time; a new one replaces the old. */
export function ToastProvider({ children }: { children: ReactNode }) {
  const [item, setItem] = useState<ToastItem | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout>>(undefined);
  const mobile = useIsMobile();
  const dismiss = useCallback(() => setItem(null), []);
  const toast = useCallback<Ctx['toast']>((message, opts = {}) => {
    clearTimeout(timer.current);
    setItem({ id: Date.now(), message, undo: opts.undo, action: opts.action });
    timer.current = setTimeout(() => setItem(null), opts.ms ?? (opts.undo ? 6000 : 3200));
  }, []);
  return (
    <ToastContext.Provider value={{ toast, dismiss }}>
      {children}
      {item ? (
        <div
          className={mobile ? 'm-toast' : 'toast'}
          style={mobile ? { position: 'fixed' } : { position: 'fixed' }}
          role="status"
          aria-live="polite"
          key={item.id}
        >
          <span>{item.message}</span>
          {mobile ? <span className="sp" /> : null}
          {item.undo ? (
            <button
              onClick={() => {
                item.undo!();
                setItem(null);
              }}
            >
              Undo
            </button>
          ) : null}
          {item.action ? (
            <button
              onClick={() => {
                item.action!.run();
                setItem(null);
              }}
            >
              {item.action.label}
            </button>
          ) : null}
        </div>
      ) : null}
    </ToastContext.Provider>
  );
}
